import React, {
  FunctionComponent,
  useContext,
  useState,
  useEffect,
} from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import {
  useTranslate,
  sendTealiumEvent,
  CONSTANTS,
  IntlContext,
} from "../../../helpers";
import routes from "../../../app/routes";
import { Pharmacy, PharmacyType, ActionType } from "../../../@types";
import { AppStore } from "../../../app/store";
import StickyTray from "../../../components/StickyTray";
import { createGa4Event, Ga4Event } from "../../../helpers/analytics";
import { useFlags } from "launchdarkly-react-client-sdk";
import { SimplePharamcyNetworkStatus } from ".";
import { toTitleCase } from "../../../helpers/formatHelpers";

interface FooterProps extends RouteComponentProps {
  hasMailOrder: boolean;
  isInNetwork: boolean;
  isMailOrderInNetwork: boolean;
  isMailOrderPreferred: boolean;
  nextRoute?: unknown | string;
  onRemoveMailOrder: () => void;
  onRemovePharmacyFromSelection: (npi: string | number) => void;
  pageLoading?: boolean;
  selectionList: Pharmacy[];
  warningMessage?: string;
}

const PharmacySelectionFooter: FunctionComponent<FooterProps> = props => {
  const t = useTranslate();
  const fm = useContext(IntlContext).formatMessage;
  const { state, dispatch } = useContext(AppStore.AppContext);
  const [footerErrorMessage, setfooterErrorMessage] = useState("");
  const [isValid, setIsValid] = useState(
    Boolean(props.selectionList.length) || props.hasMailOrder
  );
  const nextRoute = props.nextRoute as string;
  const flags = useFlags();

  useEffect(() => {
    setIsValid(Boolean(props.selectionList.length) || props.hasMailOrder);
  }, [props.selectionList, props.hasMailOrder]);

  const handleDone = (): void => {
    // istanbul ignore next: tested elsewhere
    sendTealiumEvent("Find Plans - Add Pharmacy", "CTA: Done - Next Step");

    if (!props.isInNetwork) {
      sendTealiumEvent(
        "Find Plans - Add Pharmacy",
        `Number of pharmacies submitted ${props.selectionList.length}`
      );
      createGa4Event({
        event_name: Ga4Event.PHARMACY_SELECTED,
        state,
        flags,
        search_result_count: props.selectionList.length,
      });
    } else {
      createGa4Event({
        event_name: Ga4Event.PHARMACY_SELECTED,
        state,
        flags,
        click_text: "Done",
        pharmacy_count: props.hasMailOrder
          ? props.selectionList.length + 1
          : props.selectionList.length,
      });
    }

    const { search } = props.location;

    dispatch({
      type: ActionType.SAVE_PHARMACIES,
    });

    // Adjust the pharmacyType based on what is selected
    const retailSelected = !!props.selectionList.length;
    const mailSelected = props.hasMailOrder;
    let newType;
    if (
      retailSelected &&
      mailSelected &&
      state.pharmacyType !== PharmacyType.MAIL_AND_RETAIL
    ) {
      newType = PharmacyType.MAIL_AND_RETAIL;
    } else if (
      retailSelected &&
      !mailSelected &&
      state.pharmacyType !== PharmacyType.RETAIL
    ) {
      newType = PharmacyType.RETAIL;
    } else if (
      !retailSelected &&
      mailSelected &&
      state.pharmacyType !== PharmacyType.MAIL
    ) {
      newType = PharmacyType.MAIL;
    }

    if (newType) {
      dispatch({
        type: ActionType.UPDATE_PHARMACY_TYPE,
        payload: newType,
      });
    }

    const defaultNextRoute = `${routes.searchResults}${search}`;
    props.history.push(nextRoute || defaultNextRoute);
  };

  const items = props.selectionList.map(p => ({
    name: toTitleCase(p.name.toLowerCase()),
    id: p.npi,
    subHeader: props.isInNetwork ? (
      <SimplePharamcyNetworkStatus
        inNetwork={p.in_network || false}
        preferred={p.preferred || false}
      />
    ) : null,
  }));
  if (props.hasMailOrder) {
    items.push({
      name: t("plan_search_prefs.mail_order_pharmacy"),
      id: "",
      subHeader: props.isInNetwork ? (
        <SimplePharamcyNetworkStatus
          inNetwork={props.isMailOrderInNetwork}
          preferred={props.isMailOrderPreferred}
        />
      ) : null,
    });
  }

  let screenReaderText = "";
  const totalSelected =
    props.selectionList.length + (props.hasMailOrder ? 1 : 0);
  if (totalSelected < CONSTANTS.pharmacySelection.max - 1) {
    screenReaderText = fm(
      {
        id: "pharmacy_selection_page.footer.select_more_pharmacies",
      },
      { number: CONSTANTS.pharmacySelection.max - totalSelected }
    );
  } else if (totalSelected === CONSTANTS.pharmacySelection.max - 1) {
    screenReaderText = t("pharmacy_selection_page.footer.one_more");
  }

  const handleSetErrorMessage = () => {
    setfooterErrorMessage(t("pharmacy_selection_page.no_pharmacies_selected"));
  };

  useEffect(() => {
    setfooterErrorMessage("");
  }, [footerErrorMessage]);

  return (
    <StickyTray
      id="pharmacy-selection-footer"
      className="PharmacySelectionFooter"
      heading={`${items.length} ${
        items.length === 1
          ? t("pharmacy_selection_page.footer.heading.singular")
          : t("pharmacy_selection_page.footer.heading.plural")
      }`}
      items={items}
      maxItems={CONSTANTS.pharmacySelection.max}
      onRemoveItem={(id: string | number): void => {
        if (id) {
          props.onRemovePharmacyFromSelection(id);
        } else {
          props.onRemoveMailOrder();
        }
      }}
      pageLoading={props.pageLoading}
      isValid={isValid}
      onSubmit={() => {
        !isValid ? handleSetErrorMessage() : handleDone();
      }}
      linkForSubmit={nextRoute}
      submitText={t("done")}
      submitClass="e2e-pharmacy-done"
      warningMessage={props.warningMessage}
      errorMessage={
        footerErrorMessage && (
          <span className="e2e-empty-pharmacy-selection-error">
            {footerErrorMessage}
          </span>
        )
      }
      screenReaderText={screenReaderText}
      mobileOpenButtonText={
        items.length === 1
          ? t("pharmacy_selection_page.footer.mobile_open.singular")
          : fm(
              {
                id: "pharmacy_selection_page.footer.mobile_open.plural",
              },
              { number: items.length }
            )
      }
      onMobileToggleVisibility={(visibility: boolean): void => {
        if (visibility) {
          sendTealiumEvent(
            "Find Plans - Add Pharmacy",
            "CTA: View Your Pharmacies"
          );
        } else {
          sendTealiumEvent("Find Plans - Add Pharmacy", "Close Overlay");
        }
      }}
      notStickyOnMobile
    />
  );
};

export default withRouter(PharmacySelectionFooter);
